/**
 * Public category page showing essays in a specific category.
 * 
 * Features:
 * - Category header with description
 * - List of essays in the category
 * - Link back to all categories
 * 
 * Called by:
 * - App.tsx as /category/:slug route
 */
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Typography, Spin, Alert, Card, Tag, Space, Row, Col, Pagination, Breadcrumb } from 'antd';
import { CalendarOutlined, ClockCircleOutlined, HomeOutlined } from '@ant-design/icons';
import { getPublicEssays, getPublicCategories, type PublicEssayListItem } from '../../services/api';
import { format } from 'date-fns';

const { Title, Text, Paragraph } = Typography;

const PAGE_SIZE = 12;

/**
 * Essay card component.
 */
function EssayCard({ essay }: { essay: PublicEssayListItem }) {
  return (
    <Link to={`/essays/${essay.slug}`} style={{ textDecoration: 'none' }}>
      <Card
        hoverable
        style={{
          background: 'rgba(255, 255, 255, 0.03)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          height: '100%',
          transition: 'all 0.3s ease',
        }}
        bodyStyle={{ padding: '24px' }}
        className="essay-card"
      >
        <Title
          level={4}
          style={{
            color: '#f5f5f5',
            marginBottom: '8px',
            fontFamily: "var(--font-serif)",
          }}
        >
          {essay.title}
        </Title>

        <Paragraph
          ellipsis={{ rows: 3 }}
          style={{
            color: '#a0a0a0',
            fontSize: '0.95rem',
            lineHeight: 1.7,
            marginBottom: '16px',
          }}
        >
          {essay.preview}
        </Paragraph>

        <Space size="middle" style={{ color: '#666' }}>
          {essay.publishedAt && (
            <span>
              <CalendarOutlined style={{ marginRight: '4px' }} />
              {format(new Date(essay.publishedAt), 'MMM d, yyyy')}
            </span>
          )}
          <span>
            <ClockCircleOutlined style={{ marginRight: '4px' }} />
            {essay.readingTime} min read
          </span>
        </Space>

        {essay.tags.length > 0 && (
          <div style={{ marginTop: '12px' }}>
            {essay.tags.slice(0, 3).map((tag) => (
              <Tag
                key={tag.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: '#888',
                  fontSize: '0.75rem',
                }}
              >
                {tag.name}
              </Tag>
            ))}
          </div>
        )}
      </Card>
    </Link>
  );
}

/**
 * Public category page component.
 */
function PublicCategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState(1);

  // Fetch essays in category
  const {
    data: essaysData,
    isLoading: essaysLoading,
    error: essaysError,
  } = useQuery({
    queryKey: ['public', 'essays', 'category', slug, page],
    queryFn: () => getPublicEssays({
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      category: slug,
    }),
    enabled: !!slug,
  });

  // Fetch category info
  const { data: categories } = useQuery({
    queryKey: ['public', 'categories'],
    queryFn: getPublicCategories,
  });

  const category = categories?.find((c) => c.slug === slug);

  function handlePageChange(newPage: number) {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Loading state
  if (essaysLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Error state
  if (essaysError) {
    return (
      <Alert
        message="Category not found"
        description="The category you're looking for doesn't exist."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: '24px' }}
        items={[
          { title: <Link to="/"><HomeOutlined /></Link> },
          { title: <Link to="/categories">Categories</Link> },
          { title: category?.name || slug },
        ]}
      />

      {/* Category header */}
      <div style={{ marginBottom: '48px' }}>
        <Title
          style={{
            fontSize: '2.5rem',
            fontFamily: "var(--font-serif)",
            fontWeight: 400,
            color: '#f5f5f5',
            marginBottom: '8px',
          }}
        >
          {category?.name || slug}
        </Title>
        {category?.description && (
          <Paragraph style={{ color: '#a0a0a0', fontSize: '1.1rem' }}>
            {category.description}
          </Paragraph>
        )}
        <Text type="secondary">
          {essaysData?.total || 0} {essaysData?.total === 1 ? 'essay' : 'essays'}
        </Text>
      </div>

      {/* Empty state */}
      {essaysData && essaysData.essays.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px' }}>
          <Text type="secondary">No essays in this category yet.</Text>
        </div>
      )}

      {/* Essay grid */}
      {essaysData && essaysData.essays.length > 0 && (
        <>
          <Row gutter={[24, 24]}>
            {essaysData.essays.map((essay) => (
              <Col key={essay.id} xs={24} md={12} lg={8}>
                <EssayCard essay={essay} />
              </Col>
            ))}
          </Row>

          {/* Pagination */}
          {essaysData.total > PAGE_SIZE && (
            <div style={{ textAlign: 'center', marginTop: '48px' }}>
              <Pagination
                current={page}
                total={essaysData.total}
                pageSize={PAGE_SIZE}
                onChange={handlePageChange}
                showSizeChanger={false}
              />
            </div>
          )}
        </>
      )}

      <style>{`
        .essay-card:hover {
          border-color: rgba(212, 175, 55, 0.3) !important;
          transform: translateY(-4px);
        }
      `}</style>
    </div>
  );
}

export default PublicCategoryPage;
